import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  FURNITURE_CATEGORIES,
  FURNITURE_CONDITIONS,
  mockFurnitureItems,
} from '../mocks/furnitureData';

const listingEmphasisText = '#3d2914';

function Furniture() {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [condition, setCondition] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const filteredItems = useMemo(() => {
    let items = [...mockFurnitureItems];

    if (category !== 'All') {
      items = items.filter((item) => item.category === category);
    }
    if (condition) {
      items = items.filter((item) => item.condition === condition);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      items = items.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q) ||
          item.location.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'price_asc') {
      items.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_desc') {
      items.sort((a, b) => b.price - a.price);
    }

    return items;
  }, [searchTerm, category, condition, sortBy]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" gutterBottom>
        Secondhand Furniture
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Affordable pre-loved furniture for your new place — powered by our partner marketplace.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Partner API coming soon. Showing sample listings for now.
      </Alert>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search furniture, location…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Condition</InputLabel>
          <Select
            label="Condition"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            <MenuItem value="">Any</MenuItem>
            {FURNITURE_CONDITIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort</InputLabel>
          <Select
            label="Sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="price_asc">Price: Low to High</MenuItem>
            <MenuItem value="price_desc">Price: High to Low</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
      >
        {FURNITURE_CATEGORIES.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            clickable
            color={category === cat ? 'primary' : 'default'}
            variant={category === cat ? 'filled' : 'outlined'}
            onClick={() => setCategory(cat)}
          />
        ))}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredItems.length} item{filteredItems.length === 1 ? '' : 's'}
      </Typography>

      {filteredItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" gutterBottom>
            No furniture found
          </Typography>
          <Typography color="text.secondary" paragraph>
            Try a different category or search term.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchTerm('');
              setCategory('All');
              setCondition('');
            }}
          >
            Clear filters
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={item.image}
                  alt={item.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="h6" sx={{ fontSize: '1.05rem', lineHeight: 1.3 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: listingEmphasisText, fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      ${item.price}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <Chip label={item.category} size="small" />
                    <Chip label={item.condition} size="small" variant="outlined" />
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {item.location}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default Furniture;
